import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { ComparisonHistory, ComparisonResult } from '../models/comparison-history.model';
import { environment } from '../environments/environmentCAS';

/**
 * 📡 SERVICE D'HISTORIQUE DES COMPARAISONS
 * 
 * ✅ STOCKAGE: Base de données MySQL UNIQUEMENT (via Spring Boot)
 * ❌ PAS DE LOCALSTORAGE
 * 
 * Toutes les opérations (lecture, écriture, suppression) sont effectuées
 * directement sur la base de données via l'API REST.
 * 
 * Endpoint API: http://localhost:8081/api/historique-comparaisons-ia
 */
@Injectable({
  providedIn: 'root'
})
export class ComparisonHistoryService {
  private readonly API_URL = environment.casApiUrl + '/api/historique-comparaisons-ia';
  private historySubject = new BehaviorSubject<ComparisonHistory[]>([]);
  private lastSavedId: string | null = null;
  private comparisonToLoad: ComparisonHistory | null = null;
  
  constructor(private http: HttpClient) {
    // 📡 Charger UNIQUEMENT depuis la base de données MySQL
    this.loadHistoryFromDatabase();
  }

  /**
   * Méthodes pour charger une comparaison existante dans le formulaire
   */
  setComparisonToLoad(comparison: ComparisonHistory): void {
    this.comparisonToLoad = comparison;
  }

  getComparisonToLoad(): ComparisonHistory | null {
    return this.comparisonToLoad;
  }

  clearComparisonToLoad(): void {
    this.comparisonToLoad = null;
  }

  /**
   * Obtenir l'historique des comparaisons
   */
  getHistory(): Observable<ComparisonHistory[]> {
    return this.historySubject.asObservable();
  }

  /**
   * Ajouter une nouvelle comparaison à l'historique
   */
  addComparison(comparisonData: {
    userName: string;
    userEmail: string;
    formData: any;
    results: any[];
    emailValidity?: string;
    /** Instantané brut du formulaire, pour la reprise de saisie. */
    formulaire?: any;
  }): void {
    const newComparison: ComparisonHistory = {
      id: this.generateId(), // ID temporaire, sera remplacé par l'ID de la BD
      date: new Date(),
      userName: comparisonData.userName,
      userEmail: comparisonData.userEmail,
      
      // Informations de l'assuré principal
      principalName: comparisonData.formData.nomPrincipal || 'Non renseigné',
      principalFirstName: comparisonData.formData.prenomPrincipal || 'Non renseigné',
      principalCivility: comparisonData.formData.civilitePrincipal || comparisonData.formData.civilite || '',
      principalPhone: comparisonData.formData.telephone || comparisonData.formData.telephoneFixe || '',
      principalBirthDate: comparisonData.formData.dateNaissancePrincipal || '',
      principalAge: this.calculateAge(comparisonData.formData.dateNaissancePrincipal),
      
      // Informations du conjoint.
      //
      // ANOMALIE CORRIGEE : la condition testait `situationFamiliale === 'marie'`
      // (ou 'pacs', 'concubin'), alors que ce champ contient un LIBELLÉ produit
      // par `calculateSituationFamiliale` : « Couple », « Famille (conjoint +
      // enfants) », « Célibataire »… La condition n'était donc jamais vraie et
      // aucun conjoint n'était jamais enregistré, même quand il y en avait un.
      //
      // La présence d'un conjoint se déduit de ses données, comme partout
      // ailleurs dans le composant (`hasConjoint = conjoint.dateNaissance`).
      conjoint: comparisonData.formData.dateNaissanceConjoint ? {
        name: comparisonData.formData.nomConjoint || '',
        firstName: comparisonData.formData.prenomConjoint || '',
        birthDate: comparisonData.formData.dateNaissanceConjoint,
        age: this.calculateAge(comparisonData.formData.dateNaissanceConjoint)
      } : undefined,
      
      // Informations des enfants
      enfants: comparisonData.formData.nombreEnfants > 0 ? {
        count: comparisonData.formData.nombreEnfants,
        details: this.extractEnfantsDetails(comparisonData.formData)
      } : undefined,
      
      // Détails de la comparaison
      regimeObligatoire: comparisonData.formData.regimeObligatoire || '',
      codePostal: comparisonData.formData.codePostal || '',
      dateEffet: comparisonData.formData.dateEffet || '',
      situationFamiliale: comparisonData.formData.situationFamiliale || '',
      
      // Résultats
      results: this.formatResults(comparisonData.results),
      totalResults: comparisonData.results.length,
      
      // Nouvelles infos
      origine: 'web',
      emailValidity: comparisonData.emailValidity || 'untested',

      // Instantané complet du formulaire : c'est lui qui permet de rouvrir un
      // projet avec TOUS ses champs (adresse, lieu de naissance, catégorie
      // socioprofessionnelle, curseurs de garanties…). Les champs à plat
      // ci-dessus n'en couvrent qu'une dizaine et servent à l'affichage de la
      // liste d'historique.
      formulaire: comparisonData.formulaire
    } as ComparisonHistory;

    // 📡 Enregistrer UNIQUEMENT dans la base de données
    const uniqueTimestamp = new Date().toISOString();
    console.log('📡 Enregistrement dans la base de données uniquement...');
    console.log('🔑 TIMESTAMP UNIQUE APPEL API:', uniqueTimestamp);
    
    this.saveToDatabase(newComparison).subscribe({
      next: (response) => {
        console.log('✅ Historique enregistré dans la base de données, ID:', response.id);
        this.lastSavedId = response.id;
      },
      error: (err) => {
        console.error('❌ Erreur lors de l\'enregistrement dans la BD:', err);
      }
    });
  }
  
  /**
   * Charger l'historique depuis la base de données MySQL
   */
  loadHistoryFromDatabase(): void {
    console.log('📥 Chargement de l\'historique depuis la base de données...');
    console.log('🔍 URL API:', this.API_URL);
    
    this.http.get<any[]>(this.API_URL).subscribe({
      next: (data) => {
        console.log('✅ Historique reçu de la BD:', data);
        console.log('🔍 Premier élément brut:', JSON.stringify(data[0], null, 2));
        console.log('🔍 Clés disponibles:', data[0] ? Object.keys(data[0]) : 'Aucune donnée');
        
        // Convertir les données de la BD au format ComparisonHistory
        const history: ComparisonHistory[] = data.map(item => {
          // Parser les champs JSON stockés comme string
          let conjoint = undefined;
          let enfants = undefined;
          let results = [];
          
          // Le backend renvoie les colonnes en snake_case (`conjoint_info`,
          // `enfants_info`). Ces deux clés manquaient ici : un conjoint ou des
          // enfants réellement enregistrés n'étaient jamais réaffichés.
          const lireJson = (...valeurs: any[]): any => {
            for (const valeur of valeurs) {
              if (!valeur) continue;
              if (typeof valeur === 'string') {
                try {
                  return JSON.parse(valeur);
                } catch {
                  continue;
                }
              }
              return valeur;
            }
            return undefined;
          };

          conjoint = lireJson(item.conjointInfo, item.conjoint_info, item.conjoint);
          enfants = lireJson(item.enfantsInfo, item.enfants_info, item.enfants);
          
          try {
            if (item.results && typeof item.results === 'string') {
              results = JSON.parse(item.results);
            } else if (Array.isArray(item.results)) {
              results = item.results;
            }
          } catch (e) {
            console.warn('Erreur parsing results:', e);
          }
          
          return {
            id: item.id?.toString() || this.generateId(),
            // ⚠️ CORRECTION: date_comparaison en snake_case
            date: item.dateComparaison || item.date_comparaison ? new Date(item.dateComparaison || item.date_comparaison) : new Date(),
            // ⚠️ CORRECTION: Supporter à la fois camelCase et snake_case
            userName: item.userName || item.user_name || 'Non renseigné',
            userEmail: item.userEmail || item.user_email || '',
            principalName: item.principalName || item.principal_name || 'Non renseigné',
            principalFirstName: item.principalFirstName || item.principal_first_name || '',
            principalCivility: item.principalCivility || item.principal_civility || '',
            principalBirthDate: item.principalBirthDate || item.principal_birth_date || '',
            principalAge: item.principalAge || item.principal_age || 0,
            // Un objet conjoint vide (ou sans date de naissance) ne vaut pas
            // un conjoint : sinon la fiche affichait un bloc « Conjoint »
            // rempli de « Non renseigné » pour un dossier sans conjoint.
            conjoint: conjoint?.birthDate ? {
              name: conjoint.name || '',
              firstName: conjoint.firstName || '',
              birthDate: conjoint.birthDate,
              age: conjoint.age || this.calculateAge(conjoint.birthDate)
            } : undefined,
            enfants: enfants?.count ? {
              count: enfants.count,
              details: enfants.details || []
            } : undefined,
            // ⚠️ CORRECTION: Supporter à la fois camelCase et snake_case
            regimeObligatoire: item.regimeObligatoire || item.regime_obligatoire || '',
            codePostal: item.codePostal || item.code_postal || '',
            dateEffet: item.dateEffet || item.date_effet || '',
            situationFamiliale: item.situationFamiliale || item.situation_familiale || '',
            results: results,
            totalResults: item.totalResults || item.total_results || 0,
            origine: item.origine || 'web',
            emailValidity: item.emailValidity || item.email_validity || 'untested',
            formulaire: lireJson(item.formulaire)
          } as ComparisonHistory;
        });
        
        console.log('✅ Historique formaté:', history);
        this.historySubject.next(history);
      },
      error: (err) => {
        console.error('❌ Erreur lors du chargement de l\'historique depuis la BD:', err);
        console.error('❌ Détails de l\'erreur:', err.error);
        console.error('❌ Status:', err.status);
        console.warn('⚠️ Impossible de charger l\'historique. Vérifiez que le backend est démarré sur le port 8081.');
        // Pas de fallback localStorage - uniquement la base de données
        this.historySubject.next([]);
      }
    });
  }
  
  /**
   * Enregistrer dans la base de données MySQL via Spring Boot
   */
  private saveToDatabase(comparison: ComparisonHistory): Observable<any> {
    const payload = {
      userName: comparison.userName,
      userEmail: comparison.userEmail,
      principalName: comparison.principalName,
      principalFirstName: comparison.principalFirstName,
      principalCivility: comparison.principalCivility,
      principalBirthDate: comparison.principalBirthDate,
      principalAge: comparison.principalAge,
      conjoint: comparison.conjoint,
      enfants: comparison.enfants,
      regimeObligatoire: comparison.regimeObligatoire,
      codePostal: comparison.codePostal,
      dateEffet: comparison.dateEffet,
      situationFamiliale: comparison.situationFamiliale,
      results: comparison.results,
      totalResults: comparison.totalResults,
      origine: comparison.origine || 'web',
      emailValidity: comparison.emailValidity || 'untested',
      formulaire: (comparison as any).formulaire
    };
    
    console.log('🔍 DEBUG Angular - Payload envoyé à l\'API:', payload);
    console.log('🔍 DEBUG Angular - URL:', this.API_URL);
    console.log('🔍 DEBUG Angular - Comparison complète:', comparison);
    
    return this.http.post(this.API_URL, payload);
  }
  
  /**
   * Met à jour les résultats de la dernière comparaison enregistrée
   */
  updateLastSavedResults(results: any[]): void {
    if (!this.lastSavedId) {
      console.warn('⚠️ Aucun ID d\'historique récent pour mettre à jour les résultats');
      return;
    }
    
    const formattedResults = this.formatResults(results);
    
    this.http.put(`${this.API_URL}/${this.lastSavedId}/results`, formattedResults).subscribe({
      next: (response) => {
        console.log('✅ Historique mis à jour avec les devis sélectionnés', response);
      },
      error: (err) => {
        console.error('❌ Erreur lors de la mise à jour des résultats dans la BD:', err);
      }
    });
  }
  
  /**
   * Supprimer de la base de données MySQL via Spring Boot
   */
  private deleteFromDatabase(id: string): Observable<any> {
    return this.http.delete(`${this.API_URL}/${id}`);
  }

  /**
   * Supprimer une comparaison de l'historique
   */
  deleteComparison(id: string): void {
    // 🗑️ Supprimer de la base de données
    this.deleteFromDatabase(id).subscribe({
      next: () => {
        console.log('✅ Comparaison supprimée de la base de données, ID:', id);
        // Mettre à jour la liste locale après suppression réussie
        const history = this.historySubject.value.filter(item => item.id !== id);
        this.historySubject.next(history);
      },
      error: (err) => console.error('❌ Erreur lors de la suppression dans la BD:', err)
    });
  }

  /**
   * Supprimer plusieurs comparaisons de l'historique
   */
  deleteMultipleComparisons(ids: string[]): void {
    // 🗑️ Supprimer de la base de données
    let deletedCount = 0;
    ids.forEach(id => {
      this.deleteFromDatabase(id).subscribe({
        next: () => {
          console.log('✅ Comparaison supprimée de la base de données, ID:', id);
          deletedCount++;
          // Mettre à jour la liste locale après toutes les suppressions
          if (deletedCount === ids.length) {
            const history = this.historySubject.value.filter(item => !ids.includes(item.id));
            this.historySubject.next(history);
          }
        },
        error: (err) => console.error('❌ Erreur lors de la suppression dans la BD:', err)
      });
    });
  }

  /**
   * Vider tout l'historique
   */
  clearHistory(): void {
    const currentHistory = this.historySubject.value;
    
    // 🗑️ Supprimer tout de la base de données
    let deletedCount = 0;
    currentHistory.forEach(item => {
      this.deleteFromDatabase(item.id).subscribe({
        next: () => {
          console.log('✅ Comparaison supprimée de la base de données, ID:', item.id);
          deletedCount++;
          // Vider la liste locale après toutes les suppressions
          if (deletedCount === currentHistory.length) {
            this.historySubject.next([]);
          }
        },
        error: (err) => console.error('❌ Erreur lors de la suppression dans la BD:', err)
      });
    });
  }

  /**
   * Obtenir une comparaison par ID
   */
  getComparisonById(id: string): ComparisonHistory | undefined {
    return this.historySubject.value.find(item => item.id === id);
  }

  /**
   * Filtrer l'historique par utilisateur
   */
  getHistoryByUser(userEmail: string): ComparisonHistory[] {
    return this.historySubject.value.filter(item => item.userEmail === userEmail);
  }

  /**
   * Obtenir les statistiques de l'historique
   */
  getStatistics(): {
    totalComparisons: number;
    uniqueUsers: number;
    mostActiveUser: string;
    averageResultsPerComparison: number;
  } {
    const history = this.historySubject.value;
    const userCounts: { [email: string]: number } = {};
    
    history.forEach(item => {
      userCounts[item.userEmail] = (userCounts[item.userEmail] || 0) + 1;
    });
    
    const mostActiveUser = Object.keys(userCounts).reduce((a, b) => 
      userCounts[a] > userCounts[b] ? a : b, ''
    );
    
    const totalResults = history.reduce((sum, item) => sum + item.totalResults, 0);
    
    return {
      totalComparisons: history.length,
      uniqueUsers: Object.keys(userCounts).length,
      mostActiveUser,
      averageResultsPerComparison: history.length > 0 ? totalResults / history.length : 0
    };
  }

  private generateId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }

  private calculateAge(birthDate: string): number {
    if (!birthDate) return 0;
    
    let birth: Date;
    
    // Gérer les différents formats de date
    if (birthDate.includes('/')) {
      // Format français JJ/MM/AAAA
      const parts = birthDate.split('/');
      if (parts.length === 3) {
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1; // Les mois commencent à 0 en JavaScript
        const year = parseInt(parts[2], 10);
        birth = new Date(year, month, day);
      } else {
        return 0;
      }
    } else {
      // Format ISO ou autre
      birth = new Date(birthDate);
    }
    
    // Vérifier si la date est valide
    if (isNaN(birth.getTime())) {
      console.warn('Date de naissance invalide:', birthDate);
      return 0;
    }
    
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    
    return age;
  }

  private extractEnfantsDetails(formData: any): Array<{ birthDate: string; age: number }> {
    const enfants = [];
    
    for (let i = 1; i <= formData.nombreEnfants; i++) {
      const birthDate = formData[`dateNaissanceEnfant${i}`] || '';
      enfants.push({
        birthDate,
        age: this.calculateAge(birthDate)
      });
    }
    
    return enfants;
  }

  private formatResults(results: any[]): ComparisonResult[] {
    return results.map(result => {
      console.log('🔍 DEBUG - Formatage résultat:', result);
      
      // Extraire l'assureur
      let assureur = result.assureur || result.insurerName || result.assurance || 'Non spécifié';
      
      // Extraire la formule
      let formule = result.formule || result.formula || result.niveau || 'Non spécifiée';
      
      // Extraire le prix - essayer plusieurs champs possibles
      let prix = 0;
      if (result.prix) prix = result.prix;
      else if (result.price) prix = result.price;
      else if (result.tarifMensuel) prix = result.tarifMensuel;
      else if (result.tarifGlobal) prix = result.tarifGlobal;
      else if (typeof result === 'string' && result.includes('€')) {
        // Extraire le prix d'une chaîne comme "123€/mois"
        const match = result.match(/(\d+(?:,\d+)?)\s*€/);
        if (match) prix = parseFloat(match[1].replace(',', '.'));
      }
      
      // Extraire les garanties
      let garanties = result.garanties || result.guarantees || {};
      
      const formattedResult = {
        assureur,
        formule,
        prix,
        garanties,
        selectedForQuote: !!result.selectedForQuote,
        selectedForEmail: !!result.selectedForEmail
      };
      
      console.log('✅ DEBUG - Résultat formaté:', formattedResult);
      return formattedResult;
    });
  }

  /**
   * Importe un fichier CSV d'historique
   */
  importCsv(file: File): Observable<{ message: string; imported: number; skipped: number; doublons: number; errors: string[] }> {
    const token = localStorage.getItem('access_token');
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`
    });

    const formData = new FormData();
    formData.append('file', file);

    return this.http.post<{ message: string; imported: number; skipped: number; doublons: number; errors: string[] }>(
      `${this.API_URL}/import-csv`,
      formData,
      { headers }
    );
  }

  // ❌ MÉTHODES LOCALSTORAGE SUPPRIMÉES
  // Plus besoin de loadHistory() et saveHistory()
  // Tout est géré par la base de données MySQL
}
